import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const isHome = location.pathname === '/';
  const currentLang = i18n.language;

  const toggleLanguage = () => {
    i18n.changeLanguage(currentLang === 'ja' ? 'en' : 'ja');
  };

  const navLinks = [
    { path: '/', label: t('nav_home') },
    { path: '/menu', label: t('nav_menu') },
    { path: '/about', label: t('nav_about') },
    { path: '/access', label: t('nav_access') },
  ];

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
        scrolled || !isHome
          ? 'bg-background-50/95 backdrop-blur-md border-b border-secondary-200/60'
          : 'bg-transparent'
      }`}
    >
      <nav className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-20 max-w-7xl mx-auto">
          {/* Logo / Brand */}
          {currentLang !== 'ja' && (
            <Link
              to="/"
              className={`font-heading text-lg tracking-widest transition-colors duration-300 whitespace-nowrap ${
                scrolled || !isHome
                  ? 'text-foreground-900'
                  : 'text-white'
              }`}
            >
              Takazushi
            </Link>
          )}

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-1 ml-auto">
            {navLinks.map((link) => {
              const isActive = location.pathname === link.path;
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`relative px-4 py-2 text-sm tracking-widest whitespace-nowrap transition-colors duration-300 font-label ${
                    scrolled || !isHome
                      ? isActive
                        ? 'text-foreground-900'
                        : 'text-foreground-600 hover:text-foreground-900'
                      : isActive
                        ? 'text-white'
                        : 'text-white/80 hover:text-white'
                  }`}
                >
                  {link.label}
                  {isActive && (
                    <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-4 h-[2px] bg-accent-500 rounded-full" />
                  )}
                </Link>
              );
            })}
            <Link
              to="/reservation"
              className="ml-4 px-6 py-2.5 bg-primary-500 text-background-50 text-sm tracking-widest whitespace-nowrap rounded-md hover:bg-primary-600 transition-all duration-300 font-label"
            >
              {t('nav_reservation')}
            </Link>
            <button
              onClick={toggleLanguage}
              className={`ml-4 px-3 py-2 text-xs tracking-widest whitespace-nowrap transition-colors duration-300 font-label border rounded-md ${
                scrolled || !isHome
                  ? 'text-foreground-600 border-secondary-200/80 hover:text-foreground-900 hover:border-secondary-300'
                  : 'text-white/80 border-white/30 hover:text-white hover:border-white/60'
              }`}
            >
              {currentLang === 'ja' ? t('nav_lang_en') : t('nav_lang_ja')}
            </button>
          </div>

          {/* Mobile Hamburger */}
          <div className="flex items-center gap-2 md:hidden">
            <button
              onClick={toggleLanguage}
              className={`px-2 py-1 text-[10px] tracking-widest whitespace-nowrap transition-colors duration-300 font-label border rounded ${
                scrolled || !isHome
                  ? 'text-foreground-600 border-secondary-200/80'
                  : 'text-white/80 border-white/30'
              }`}
            >
              {currentLang === 'ja' ? t('nav_lang_en') : t('nav_lang_ja')}
            </button>
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className={`w-10 h-10 flex items-center justify-center transition-colors duration-300 ${
                scrolled || !isHome ? 'text-foreground-900' : 'text-white'
              }`}
              aria-label="メニューを開く"
            >
              <i className={`text-xl transition-transform duration-300 ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`} />
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <div
          className={`md:hidden overflow-hidden transition-all duration-400 ${
            mobileOpen ? 'max-h-80 pb-4' : 'max-h-0'
          }`}
        >
          <div className="flex flex-col gap-1 bg-background-50/95 backdrop-blur-md rounded-lg border border-secondary-200/60 p-2">
            {navLinks.map((link) => {
              const isActive = location.pathname === link.path;
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-4 py-3 text-sm tracking-widest whitespace-nowrap rounded-md transition-colors ${
                    isActive
                      ? 'bg-secondary-100 text-foreground-900'
                      : 'text-foreground-600 hover:bg-secondary-50 hover:text-foreground-900'
                  }`}
                >
                  {link.label}
                </Link>
              );
            })}
            <Link
              to="/reservation"
              className="mt-2 px-4 py-3 bg-primary-500 text-background-50 text-sm tracking-widest whitespace-nowrap rounded-md text-center hover:bg-primary-600 transition-all"
            >
              {t('nav_reservation')}
            </Link>
          </div>
        </div>
      </nav>
    </header>
  );
}